import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import AuthContext from '../context/AuthContext';
import { Mail, Lock, Stethoscope, User, Building2 } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [tab, setTab] = useState('fisioterapeuta');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailNaoVerificado, setEmailNaoVerificado] = useState(false);
  const [reenviando, setReenviando] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error('Por favor, preencha todos os campos.');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      const { role, crefitoStatus } = data;

      if (tab === 'paciente' && role !== 'paciente') {
        toast.error('Esta conta não é de paciente.');
        setLoading(false); return;
      }
      if (tab === 'fisioterapeuta' && role !== 'fisioterapeuta' && role !== 'admin') {
        toast.error('Esta conta não é de fisioterapeuta.');
        setLoading(false); return;
      }
      if (tab === 'clinica' && role !== 'clinica') {
        toast.error('Esta conta não é de clínica.');
        setLoading(false); return;
      }

      login(data.token);
      toast.success('Login realizado com sucesso!');

      if (role === 'paciente') navigate('/meu-painel');
      else if (role === 'clinica') {
        if (crefitoStatus !== 'aprovado') navigate('/aguardando-aprovacao');
        else navigate('/clinica/dashboard');
      } else if (role === 'fisioterapeuta' && crefitoStatus !== 'aprovado') navigate('/aguardando-aprovacao');
      else navigate('/');
    } catch (error) {
      const code = error.response?.data?.code;
      if (code === 'EMAIL_NOT_VERIFIED') {
        setEmailNaoVerificado(true);
      } else {
        const msg = error.response?.data?.error || 'Credenciais inválidas.';
        toast.error(msg, { duration: 4000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { key: 'fisioterapeuta', label: 'Fisioterapeuta', icon: Stethoscope },
    { key: 'paciente', label: 'Paciente', icon: User },
    { key: 'clinica', label: 'Clínica', icon: Building2 },
  ];

  const handleReenviarVerificacao = async () => {
    setReenviando(true);
    try {
      await api.post('/auth/reenviar-verificacao', { email });
      toast.success('Email de verificação reenviado!');
    } catch { toast.error('Erro ao reenviar.'); }
    finally { setReenviando(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-gray-100">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-4 text-sm font-medium transition-colors border-b-2 ${
                tab === key
                  ? key === 'clinica' ? 'border-blue-600 text-blue-600 bg-blue-50/40'
                    : 'border-blue-600 text-blue-600 bg-blue-50/40'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        <div className="p-8">
          <div className="mb-6">
            <h1 className="text-xl font-bold text-gray-900">
              {tab === 'fisioterapeuta' ? 'Acesso do Fisioterapeuta'
                : tab === 'paciente' ? 'Acesso do Paciente'
                : 'Acesso da Clínica'}
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              {tab === 'fisioterapeuta' ? 'Entre com suas credenciais profissionais'
                : tab === 'paciente' ? 'Acompanhe seu tratamento'
                : 'Gerencie sua clínica'}
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seuemail@exemplo.com"
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-shadow"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sua senha"
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-shadow"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white py-2.5 rounded-xl font-medium text-sm transition-colors mt-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Entrando...
                </>
              ) : 'Entrar'}
            </button>
          </form>

          {emailNaoVerificado && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-center">
              <p className="text-sm text-amber-800 font-medium mb-2">📧 Verifique seu email antes de fazer login</p>
              <p className="text-xs text-amber-600 mb-3">Não recebeu o email?</p>
              <button onClick={handleReenviarVerificacao} disabled={reenviando}
                className="px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors">
                {reenviando ? 'Reenviando...' : 'Reenviar email de verificação'}
              </button>
            </div>
          )}

          <p className="text-center mt-3 text-sm">
            <Link to="/esqueci-senha" className="text-gray-400 hover:text-blue-600 hover:underline text-xs transition-colors">
              Esqueci minha senha
            </Link>
          </p>

          {tab === 'fisioterapeuta' && (
            <p className="text-center mt-3 text-sm text-gray-500">
              Não tem uma conta?{' '}
              <Link to="/register" className="font-medium text-blue-600 hover:underline">
                Registre-se aqui
              </Link>
            </p>
          )}
          {tab === 'clinica' && (
            <p className="text-center mt-5 text-sm text-gray-500">
              Não tem uma conta?{' '}
              <Link to="/register/clinica" className="font-medium text-blue-600 hover:underline">
                Cadastre sua clínica
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}